import { addDays } from "date-fns";
import Enumerable from "linq";
import { ConfigurationType, AccessType, Scope, DurationTimeframeTypeEnum, DateRangeType } from "../../enums/DRCXEnums";
import { AccessPermission } from "../AccessPermission";
import { IDRCXConfigurationDTOBase } from "./DRCXConfigurationDTOBase";
import { IDRCXConfigurationSectionDTOBase } from "./DRCXConfigurationSectionDTOBase";
import { DRCXConfigurationSectionModelBase } from "./DRCXConfigurationSectionModelBase";

export abstract class DRCXConfigurationModelBase<SectionType extends DRCXConfigurationSectionModelBase> {
    public static NEW_CONFIGURATION_ID: number = -1;

    // Properties
    public id: number = DRCXConfigurationModelBase.NEW_CONFIGURATION_ID;
    public name: string = "New Report";
    public configurationType: ConfigurationType = ConfigurationType.Report;
    public access: AccessType = AccessType.Private;
    public userPermissions: Array<AccessPermission> = [];
    public scope: Scope = Scope.User;
    public componentName: string | undefined = undefined;
    public procedureName: string | undefined = undefined;
    public created: Date = new Date();
    public forAllGroups: boolean = false;
    public overrideInstanceDates: boolean = false;

    // #region The following properties should use DRCXTimeAndDuration but everything would need tested again

    public durationType: DurationTimeframeTypeEnum | undefined = DurationTimeframeTypeEnum.MultipleTimeFrames;

    // durationType == SpecificStartOrStop
    public fixedStartDate: Date | undefined = addDays(new Date(), -7);
    public fixedStartDateRangeType: DateRangeType | undefined = DateRangeType.Raw;
    public fixedEndDate: Date | undefined = new Date();
    public fixedEndDateRangeType: DateRangeType | undefined = DateRangeType.Raw;

    // durationType == MultipleTimeFrames
    public multipleStartOffset: number | undefined = -6;
    public multipleStartRangeType: DateRangeType | undefined = DateRangeType.Day;
    public multipleDuration: number | undefined = 7;
    public multipleDurationRangeType: DateRangeType | undefined = DateRangeType.Day;

    // durationType == CurrentSingleTimeFrame
    public currentRangeType: DateRangeType | undefined = DateRangeType.Month;

    // durationType == PreviousSingleTimeFrame
    public previousRangeType: DateRangeType | undefined = DateRangeType.Hour;

    public runToCurrentDate: boolean | undefined = false;
    public currentDateOffset: number | undefined = 0;
    public currentDateRangeType: DateRangeType | undefined = undefined;

    // #endregion

    public customCode: string | undefined = undefined;
    public adhoc: boolean = false;
    public ownerID: number | undefined = undefined;
    public ownerName: string | undefined = undefined;
    public sectionCount: number | undefined = undefined;

    // Collections
    public sections: Array<SectionType> = new Array<SectionType>();

    constructor(dto?: IDRCXConfigurationDTOBase) {

        if (dto) {
            if (typeof dto.id !== "undefined") {
                this.id = dto.id;
            }
            if (typeof dto.name !== "undefined") {
                this.name = dto.name;
            }
            if (typeof dto.configurationType !== "undefined") {
                this.configurationType = dto.configurationType;
            }
            if (typeof dto.access !== "undefined") {
                this.access = dto.access;
            }
            if (typeof dto.userPermissions !== "undefined") {
                this.userPermissions = dto.userPermissions
                    ? dto.userPermissions.map(
                        permission => new AccessPermission(permission)
                    )
                    : new Array<AccessPermission>();
            }
            if (typeof dto.scope !== "undefined") {
                this.scope = dto.scope;
            }
            if (typeof dto.componentName !== "undefined") {
                this.componentName = dto.componentName;
            }
            if (typeof dto.procedureName !== "undefined") {
                this.procedureName = dto.procedureName;
            }
            if (typeof dto.created !== "undefined") {
                this.created = new Date(dto.created);
            }
            if (typeof dto.forAllGroups !== "undefined") {
                this.forAllGroups = dto.forAllGroups;
            }
            if (typeof dto.overrideInstanceDates !== "undefined") {
                this.overrideInstanceDates = dto.overrideInstanceDates;
            }
            if (typeof dto.timeframe !== "undefined" && dto.timeframe) {
                this.durationType = dto.timeframe.durationTimeframeType;
                if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.MultipleTimeFrames) {
                    if (typeof dto.timeframe.calculatedStartDateOffset !== "undefined") {
                        this.multipleStartOffset = dto.timeframe.calculatedStartDateOffset
                            ? dto.timeframe.calculatedStartDateOffset.duration
                            : undefined;
                        this.multipleStartRangeType = dto.timeframe.calculatedStartDateOffset
                            ? dto.timeframe.calculatedStartDateOffset.rangeType
                            : undefined;
                    }
                    if (typeof dto.timeframe.duration !== "undefined") {
                        this.multipleDuration = dto.timeframe.duration
                            ? dto.timeframe.duration.duration
                            : undefined;
                        this.multipleDurationRangeType = dto.timeframe.duration
                            ? dto.timeframe.duration.rangeType
                            : undefined;
                    }
                } else if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.CurrentSingleTimeFrame) {
                    if (typeof dto.timeframe.duration !== "undefined") {
                        this.currentRangeType = dto.timeframe.duration
                            ? dto.timeframe.duration.rangeType
                            : undefined;
                    }
                } else if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.PreviousSingleTimeFrame) {
                    if (typeof dto.timeframe.duration !== "undefined") {
                        this.previousRangeType = dto.timeframe.duration
                            ? dto.timeframe.duration.rangeType
                            : undefined;
                    }
                } else if (dto.timeframe.durationTimeframeType ===
                    DurationTimeframeTypeEnum.SpecificStartOrStop) {
                    if (typeof dto.timeframe.fixedStartDate !== "undefined") {
                        this.fixedStartDate = dto.timeframe.fixedStartDate
                            ? new Date(dto.timeframe.fixedStartDate.timestamp)
                            : undefined;
                        this.fixedStartDateRangeType = dto.timeframe.fixedStartDate
                            ? dto.timeframe.fixedStartDate.rangeType
                            : undefined;
                    }
                    if (typeof dto.timeframe.fixedEndDate !== "undefined") {
                        this.fixedEndDate = dto.timeframe.fixedEndDate
                            ? new Date(dto.timeframe.fixedEndDate.timestamp)
                            : undefined;
                        this.fixedEndDateRangeType = dto.timeframe.fixedEndDate
                            ? dto.timeframe.fixedEndDate.rangeType
                            : undefined;
                    }
                }
                this.runToCurrentDate = dto.timeframe.runToCurrentDate;
                if (typeof dto.timeframe.currentDateOffset !== "undefined") {
                    this.currentDateOffset = dto.timeframe.currentDateOffset
                        ? dto.timeframe.currentDateOffset.duration
                        : undefined;
                    this.currentDateRangeType = dto.timeframe.currentDateOffset
                        ? dto.timeframe.currentDateOffset.rangeType
                        : undefined;
                }
            }
            if (typeof dto.customCode !== "undefined") {
                this.customCode = dto.customCode;
            }
            if (typeof dto.adhoc !== "undefined") {
                this.adhoc = dto.adhoc;
            }
            if (typeof dto.ownerID !== "undefined") {
                this.ownerID = dto.ownerID;
            }
            if (typeof dto.ownerName !== "undefined") {
                this.ownerName = dto.ownerName;
            }
            if (typeof dto.sectionCount !== "undefined") {
                this.sectionCount = dto.sectionCount;
            }
            if (typeof dto.sections !== "undefined") {
                this.sections = dto.sections
                    ? dto.sections.map(
                        section => this.createSection(section)
                    )
                    : new Array<SectionType>();
            }
        }
    }

    // public userHasPermission(permission: AccessPermission): boolean {
    //     return this.userPermissions
    //         ? Enumerable.from(this.userPermissions).any(p => p.contains(permission))
    //         : false;
    // }

    // public addSection(section: SectionType) {
    //     this.sections.push(section);
    // }

    // public removeSection(section: SectionType): boolean {
    //     const index = this.sections.indexOf(section);
    //     if (index >= 0) {
    //         this.sections.splice(index, 1);
    //         return true;
    //     }
    //     return false;
    // }

    public toDTO(): IDRCXConfigurationDTOBase {
        return this._toDTO({});
    }

    public abstract clone(): DRCXConfigurationModelBase<SectionType>;

    // public /*virtual*/ validate(): boolean {

    //     let allSectionsValid: boolean = true;

    //     if (this.sections) {
    //         this.sections.forEach(section => {
    //             if (section.validate() === false) {
    //                 allSectionsValid = false;
    //             }
    //         });
    //     }

    //     return this.sections && this.sections.length > 0 && allSectionsValid;
    // }

    public /*virtual*/ hasDeprecatedFeatures(): boolean {
        return this.sections && Enumerable.from(this.sections).any(section => section.hasDeprecatedFeatures());
    }

    protected abstract createSection(section: IDRCXConfigurationSectionDTOBase): SectionType;

    protected _toDTO(dto: IDRCXConfigurationDTOBase): IDRCXConfigurationDTOBase {
        dto.id = this.id;
        dto.name = this.name;
        dto.configurationType = this.configurationType;
        dto.access = this.access;
        dto.userPermissions = this.userPermissions
            ? this.userPermissions.map(
                permission => {
                    return {
                        functionalityType: permission.functionalityType,
                        access: permission.access
                    };
                }
            )
            : undefined;
        dto.scope = this.scope;
        dto.componentName = this.componentName;
        dto.procedureName = this.procedureName;
        dto.created = this.created.toISOString();
        dto.forAllGroups = this.forAllGroups;
        dto.overrideInstanceDates = this.overrideInstanceDates;
        if (this.durationType === DurationTimeframeTypeEnum.MultipleTimeFrames) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: this.multipleStartOffset !== undefined && this.multipleStartRangeType !== undefined
                    ? {
                        duration: this.multipleStartOffset,
                        rangeType: this.multipleStartRangeType
                    }
                    : undefined,
                duration: this.multipleDuration !== undefined && this.multipleDurationRangeType !== undefined
                    ? {
                        duration: this.multipleDuration,
                        rangeType: this.multipleDurationRangeType
                    }
                    : undefined,
                currentDateOffset: undefined
            };
        } else if (this.durationType === DurationTimeframeTypeEnum.CurrentSingleTimeFrame) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: 0,
                    rangeType: this.currentRangeType as DateRangeType
                },
                duration: {
                    duration: 1,
                    rangeType: this.currentRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (this.durationType === DurationTimeframeTypeEnum.PreviousSingleTimeFrame) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: false,
                calculatedStartDateOffset: {
                    duration: -1,
                    rangeType: this.previousRangeType as DateRangeType
                },
                duration: {
                    duration: 1,
                    rangeType: this.previousRangeType as DateRangeType
                },
                currentDateOffset: undefined
            };
        } else if (this.durationType === DurationTimeframeTypeEnum.SpecificStartOrStop) {
            dto.timeframe = {
                durationTimeframeType: this.durationType,
                fixedStartDate: {
                    timestamp: this.fixedStartDate?.toISOString() as string,
                    rangeType: this.fixedStartDateRangeType as DateRangeType
                },
                fixedEndDate: {
                    timestamp: this.fixedEndDate?.toISOString() as string,
                    rangeType: this.fixedEndDateRangeType as DateRangeType
                },
                runToCurrentDate: false,
                calculatedStartDateOffset: undefined,
                duration: undefined,
                currentDateOffset: undefined
            };
        } else {
            dto.timeframe = {
                durationTimeframeType: undefined,
                fixedStartDate: undefined,
                fixedEndDate: undefined,
                runToCurrentDate: undefined,
                calculatedStartDateOffset: undefined,
                duration: undefined,
                currentDateOffset: undefined
            };
        }
        dto.customCode = this.customCode;
        dto.adhoc = this.adhoc;
        dto.ownerID = this.ownerID;
        dto.ownerName = this.ownerName;
        dto.sectionCount = this.sectionCount;
        dto.sections = this.sections
            ? this.sections.map(
                section => section.toDTO()
            )
            : undefined;
        return dto;
    }
}
